import React from "react";
import {
  Button,
  Grid,
  Typography,
  Box,
  Card,
  CardContent,
  Container,
  IconButton,
  Divider,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { styled } from "@mui/material/styles";
import {
  Home as HomeIcon,
  Search as SearchIcon,
  ArrowBack as ArrowBackIcon,
  Help as HelpIcon,
} from "@mui/icons-material";

// Styled components
const StyledContainer = styled(Container)(({ theme }) => ({
  minHeight: "100vh",
  background: "linear-gradient(135deg, #f8fafc 0%, #e5e7eb 100%)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(4),
}));

const StyledCard = styled(Card)(({ theme }) => ({
  maxWidth: 600,
  width: "100%",
  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, #f8fafc 100%)`,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: "0 10px 40px rgba(0, 0, 0, 0.1)",
  borderRadius: theme.shape.borderRadius * 3,
  overflow: "hidden",
  position: "relative",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: 4,
    background: "linear-gradient(90deg, #1e3a8a 0%, #3b82f6 100%)",
  },
}));

const AnimatedNumber = styled(motion.div)(({ theme }) => ({
  fontSize: "8rem",
  fontWeight: 900,
  background: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)",
  backgroundClip: "text",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  textAlign: "center",
  lineHeight: 1,
  [theme.breakpoints.down("sm")]: {
    fontSize: "6rem",
  },
}));

const NavyButton = styled(Button)(({ theme }) => ({
  background: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)",
  color: "white",
  padding: "12px 32px",
  fontSize: "1rem",
  fontWeight: 600,
  borderRadius: theme.shape.borderRadius * 2,
  textTransform: "none",
  boxShadow: "0 4px 20px rgba(30, 58, 138, 0.3)",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    background: "linear-gradient(135deg, #1e40af 0%, #2563eb 100%)",
    boxShadow: "0 8px 30px rgba(30, 58, 138, 0.4)",
    transform: "translateY(-2px)",
  },
}));

const FloatingIcon = styled(motion.div)(({ theme }) => ({
  position: "absolute",
  opacity: 0.1,
  fontSize: "2rem",
  color: theme.palette.primary.main,
}));

export default function NotFound() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        damping: 12,
        stiffness: 200,
      },
    },
  };

  const numberVariants = {
    hidden: { scale: 0.5, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: {
        type: "spring",
        damping: 10,
        stiffness: 200,
      },
    },
  };

  const floatingIconVariants = {
    animate: {
      y: [0, -20, 0],
      rotate: [0, 10, -10, 0],
      transition: {
        duration: 4,
        repeat: Infinity,
        ease: "easeInOut",
      },
    },
  };

  return (
    <StyledContainer maxWidth={false} disableGutters>
      {/* Floating background icons */}
      <FloatingIcon
        variants={floatingIconVariants}
        animate="animate"
        style={{ top: "10%", left: "10%" }}
      >
        <SearchIcon fontSize="inherit" />
      </FloatingIcon>
      <FloatingIcon
        variants={floatingIconVariants}
        animate="animate"
        style={{ top: "20%", right: "15%", animationDelay: "1s" }}
      >
        <HelpIcon fontSize="inherit" />
      </FloatingIcon>
      <FloatingIcon
        variants={floatingIconVariants}
        animate="animate"
        style={{ bottom: "15%", left: "15%", animationDelay: "2s" }}
      >
        <HomeIcon fontSize="inherit" />
      </FloatingIcon>

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        style={{ width: "100%", maxWidth: 600 }}
      >
        <StyledCard>
          <CardContent sx={{ p: { xs: 3, md: 5 }, textAlign: "center" }}>
            {/* 404 Number */}
            <motion.div variants={numberVariants}>
              <AnimatedNumber
                animate={{
                  scale: [1, 1.02, 1],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                404
              </AnimatedNumber>
            </motion.div>

            {/* Main Title */}
            <motion.div variants={itemVariants}>
              <Typography
                variant={isMobile ? "h4" : "h3"}
                sx={{
                  fontWeight: 700,
                  color: "#111827",
                  mb: 2,
                  background:
                    "linear-gradient(135deg, #111827 0%, #374151 100%)",
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Oops! Page Not Found
              </Typography>
            </motion.div>

            {/* Description */}
            <motion.div variants={itemVariants}>
              <Typography
                variant="body1"
                sx={{
                  color: "#6b7280",
                  mb: 4,
                  fontSize: "1.1rem",
                  lineHeight: 1.6,
                  maxWidth: 400,
                  mx: "auto",
                }}
              >
                The page you're looking for doesn't exist or has been moved.
                Don't worry, it happens to the best of us!
              </Typography>
            </motion.div>

            <Divider sx={{ my: 3, opacity: 0.5 }} />

            {/* Action Buttons */}
            <motion.div variants={itemVariants}>
              <Grid container spacing={2} justifyContent="center">
                <Grid item xs={12} sm="auto">
                  <NavyButton
                    component={Link}
                    to="/"
                    variant="contained"
                    size="large"
                    startIcon={<HomeIcon />}
                    fullWidth={isMobile}
                  >
                    Go to Home
                  </NavyButton>
                </Grid>
                <Grid item xs={12} sm="auto">
                  <Button
                    variant="outlined"
                    size="large"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => window.history.back()}
                    fullWidth={isMobile}
                    sx={{
                      borderColor: "#d1d5db",
                      color: "#6b7280",
                      borderRadius: 2,
                      padding: "12px 32px",
                      fontWeight: 600,
                      textTransform: "none",
                      "&:hover": {
                        borderColor: "#1e3a8a",
                        backgroundColor: "rgba(30, 58, 138, 0.04)",
                        color: "#1e3a8a",
                      },
                    }}
                  >
                    Go Back
                  </Button>
                </Grid>
              </Grid>
            </motion.div>

            {/* Help Text */}
            <motion.div variants={itemVariants}>
              <Box sx={{ mt: 4, pt: 3, borderTop: "1px solid #f3f4f6" }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#9ca3af",
                    fontSize: "0.875rem",
                  }}
                >
                  Need help? Contact our support team or check our{" "}
                  <Link
                    to="/help"
                    style={{
                      color: "#1e3a8a",
                      textDecoration: "none",
                      fontWeight: 600,
                    }}
                  >
                    help center
                  </Link>
                </Typography>
              </Box>
            </motion.div>
          </CardContent>
        </StyledCard>
      </motion.div>
    </StyledContainer>
  );
}
